import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import Header from "./Header";
import Footer from "./Footer";
import TablePagination from "@mui/material/TablePagination";
import "./EmployeeLeaveTable.css";

const EmployeeLeaveTable = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState("upcoming-year");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/predicted_leaves_2025_with_type.csv");
        const reader = await response.text();
        Papa.parse(reader, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedData = results.data.map((row) => ({
              name: row["NAME"],
              division: row["DIV"],
              department: row["DEP"],
              location: row["LOC"],
              nextLeaveDate: new Date(row["NEXT LEAVE DATE"]),
              nextLeaveType: row["NEXT LEAVE TYPE"],
            }));

            parsedData.sort((a, b) => a.nextLeaveDate - b.nextLeaveDate);
            setData(parsedData);
            setFilteredData(parsedData.filter((row) => row.nextLeaveDate >= new Date())); // Default: Show only future dates
            setIsLoading(false);
          },
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const filterValue = e.target.value;
    setFilter(filterValue);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset time to midnight for accurate comparisons

    let filtered = data.filter((row) => row.nextLeaveDate >= now); // Default: Show only future dates

    if (filterValue === "upcoming-week") {
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(now.getDate() + 7);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneWeekFromNow);
    } else if (filterValue === "upcoming-month") {
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(now.getMonth() + 1);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneMonthFromNow);
    } else if (filterValue === "upcoming-year") {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(now.getFullYear() + 1);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneYearFromNow);
    }

    setFilteredData(filtered.filter((row) => row.name.toLowerCase().includes(searchQuery.toLowerCase())));
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to midnight

    let filtered = data.filter((row) => row.nextLeaveDate >= now); // Default: Show future dates

    if (filter === "upcoming-week") {
      const oneWeekFromNow = new Date();
      oneWeekFromNow.setDate(now.getDate() + 7);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneWeekFromNow);
    } else if (filter === "upcoming-month") {
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(now.getMonth() + 1);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneMonthFromNow);
    } else if (filter === "upcoming-year") {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(now.getFullYear() + 1);
      filtered = filtered.filter((row) => row.nextLeaveDate <= oneYearFromNow);
    }

    setFilteredData(filtered.filter((row) => row.name.toLowerCase().includes(query.toLowerCase())));
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Pagination handlers
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  return (
    <div className="app-container">
      <Header />
      <main className="content">
        <div className="maintenance-table">
          <div className="header">
            <h1>Predicted Employee Leave Dates</h1>

            <div className="controls">
              <input
                type="text"
                placeholder="Search Employee..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />

              <div className="filter-container">
                <label htmlFor="filter">Sort/Filter: </label>
                <select id="filter" value={filter} onChange={handleFilterChange} className="filter-select">
                  <option value="upcoming-week">Upcoming Week</option>
                  <option value="upcoming-month">Upcoming Month</option>
                  <option value="upcoming-year">Upcoming Year</option>
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading">
              <div className="spinner"></div>
              Loading data...
            </div>
          ) : filteredData.length === 0 ? (
            <p className="no-records">No leave records found.</p>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Division</th>
                      <th>Department</th>
                      <th>Location</th>
                      <th>Next Leave Date</th>
                      <th>Next Leave Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                      <tr key={index}>
                        <td data-label="Name">{row.name}</td>
                        <td data-label="Division">{row.division}</td>
                        <td data-label="Department">{row.department}</td>
                        <td data-label="Location">{row.location}</td>
                        <td data-label="Next Leave Date" className="center">{formatDate(row.nextLeaveDate)}</td>
                        <td data-label="Next Leave Type" className="center">
                          {row.nextLeaveType}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Always Visible */}
              <div className="pagination-container">
                <TablePagination
                  rowsPerPageOptions={[50, 100, 500, 1000, 5000, 10000]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EmployeeLeaveTable;
